// Derive which recipe ingredients belong to a given cooking step.
//
// Cooking-instruction steps carry no explicit ingredient mapping, so we infer
// it on the frontend by matching ingredient names against the step's title and
// detail text (case-insensitive, word-boundary based). Used by CookingModePage
// to render the ingredients relevant to the active step.

// Pull the searchable text out of a parsed step object.
const getStepText = (step) => {
  if (!step) return '';
  if (typeof step === 'string') return step;
  const parts = [];
  if (step.step_title) parts.push(step.step_title);
  if (step.title) parts.push(step.title);
  const details = step.details || [];
  if (Array.isArray(details)) {
    for (const d of details) {
      if (typeof d === 'string') parts.push(d);
      else if (d && d.detail) parts.push(d.detail);
    }
  }
  return parts.join(' ');
};

// Escape a string for safe use inside a RegExp.
const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Build a Unicode-aware "word boundary" regex. JS `\b` only understands ASCII
// word characters, so it never matches around Cyrillic/Greek/etc. We approximate
// boundaries with lookarounds on Unicode letters and numbers instead.
const wordBoundaryRe = (term) =>
  new RegExp(`(?<![\\p{L}\\p{N}])${escapeRegExp(term)}(?![\\p{L}\\p{N}])`, 'iu');

// --- Lightweight stemming -------------------------------------------------
// Ingredient names ("масло", "соль", "перец") rarely appear verbatim in the
// instruction text, which is written in natural grammatical forms ("оливкового
// масла", "солью", "перцем"). Exact matching therefore misses most ingredients
// in inflected languages. We strip common inflectional endings so that the
// stems line up ("масл", "сол", "перц").

const CYRILLIC_CONSONANTS = 'бвгджзйклмнпрстфхцчшщ';
const HAS_CYRILLIC = /[\u0400-\u04FF]/;

// Longest-first so multi-char endings are removed before single chars.
const RU_SUFFIXES = [
  'иями', 'ами', 'ями', 'ого', 'его', 'ому', 'ему', 'ыми', 'ими',
  'ое', 'ые', 'ый', 'ий', 'ая', 'яя', 'ую', 'юю', 'ою', 'ею', 'ие',
  'ах', 'ях', 'ам', 'ям', 'ов', 'ев', 'ом', 'ем', 'ой', 'ей', 'ью', 'ия', 'ье',
  'а', 'я', 'о', 'е', 'у', 'ю', 'ы', 'и', 'ь', 'й',
];

const fleetingVowelRe = new RegExp(
  `([${CYRILLIC_CONSONANTS}])[ео]([${CYRILLIC_CONSONANTS}])$`
);

const stemWord = (word) => {
  let s = String(word || '').toLowerCase().trim();
  if (!s) return '';

  if (HAS_CYRILLIC.test(s)) {
    for (const suffix of RU_SUFFIXES) {
      if (s.length - suffix.length >= 3 && s.endsWith(suffix)) {
        s = s.slice(0, -suffix.length);
        break;
      }
    }
    // Collapse the fleeting vowel ("перец" → "перц", matching "перцем").
    const collapsed = s.replace(fleetingVowelRe, '$1$2');
    if (collapsed.length >= 3) s = collapsed;
  } else {
    // Latin/other: drop a trailing plural marker.
    if (s.length > 4 && s.endsWith('es')) s = s.slice(0, -2);
    else if (s.length > 3 && s.endsWith('s')) s = s.slice(0, -1);
  }
  return s;
};

// Two stems are considered the same ingredient reference if they are equal,
// or one is a prefix of the other (covers spelling/length drift).
const stemsMatch = (a, b) => {
  if (!a || !b) return false;
  if (a === b) return true;
  // Prefix match only for longer stems with small length drift, to avoid
  // short-stem collisions (e.g. "sal" matching both "salt" and "salad").
  if (a.length >= 5 && b.length >= 5 && (a.startsWith(b) || b.startsWith(a))) {
    return Math.abs(a.length - b.length) <= 3;
  }
  return false;
};

// Qualifier/adjective words that should never drive a match on their own — they
// describe form/quality, not the ingredient itself. Dropping them prevents
// false positives like "Washed tender shoots mix maxi salad" leaking into an
// unrelated step.
const STOP_WORDS = new Set([
  'fresh', 'washed', 'tender', 'baby', 'mini', 'maxi', 'mix', 'mixed', 'large',
  'small', 'medium', 'extra', 'virgin', 'organic', 'free', 'range', 'whole',
  'ripe', 'raw', 'lean', 'light', 'plain', 'natural', 'pure', 'chopped',
  'sliced', 'diced', 'grated', 'shredded', 'peeled', 'seedless', 'boneless',
  'skinless', 'premium', 'quality', 'classic', 'original', 'traditional',
  'homemade',
]);

// Significant words from an ingredient name (drop short connective + qualifier words).
const significantWords = (name) =>
  String(name || '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 3 && !STOP_WORDS.has(w));

// English short-name normalizer for English↔English step-ingredient mapping.
const normEnglish = (s) => String(s || '').toLowerCase().trim();

// Stemmed significant tokens of an English name (for token-overlap matching).
const tokenStems = (s) =>
  normEnglish(s)
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 2 && !STOP_WORDS.has(w))
    .map(stemWord)
    .filter(Boolean);

// True if two English names share at least one significant (stemmed) token.
const sharesSignificantToken = (aName, bName) => {
  const a = tokenStems(aName);
  const b = tokenStems(bName);
  if (a.length === 0 || b.length === 0) return false;
  return a.some((x) => b.some((y) => stemsMatch(x, y)));
};

// Returns the subset of `ingredients` referenced by a cooking step / line.
//
// When the LLM provides an English `ingredients` array for the step (passed via
// `stepIngredientNames`), it is used as a whitelist that constrains the candidate
// pool — this stops ingredients from OTHER steps leaking in. Within that pool we
// still text-match against the active instruction line (word-boundary + stem) so
// each line only shows the products it actually references.
//
// Old recipes without per-step ingredients fall back to text matching over all
// ingredients.
export const getStepIngredients = (step, ingredients, stepIngredientNames = null) => {
  if (!Array.isArray(ingredients) || ingredients.length === 0) return [];

  // Constrain the candidate pool to the step's declared ingredients (when the
  // LLM provided them) so ingredients from OTHER steps can never leak in. We
  // still text-match within this pool against the active instruction line for
  // per-line precision. Old recipes (no per-step ingredients) use all ingredients.
  let pool = ingredients;
  if (Array.isArray(stepIngredientNames) && stepIngredientNames.length > 0) {
    const wanted = stepIngredientNames.map(normEnglish).filter(Boolean);
    if (wanted.length > 0) {
      const allowed = ingredients.filter((ing) => {
        const eng = [ing.name, ing.product].filter(Boolean);
        return eng.some((c) => wanted.some((w) => sharesSignificantToken(c, w)));
      });
      if (allowed.length > 0) pool = allowed;
    }
  }

  const text = getStepText(step).toLowerCase();
  if (!text.trim()) return [];

  // Pre-stem every word in the step text once.
  const textWordStems = text
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map(stemWord)
    .filter(Boolean);

  const matched = [];
  const seen = new Set();

  for (const ing of pool) {
    const key = ing.name || ing.product || '';
    if (!key || seen.has(key)) continue;

    // Prefer the recipe short name, fall back to the store / localized product name(s).
    // `localizedNames` may contain every available translation, since cooking steps
    // can be in a different language than the current UI.
    // Note: we intentionally do NOT spread every `localizedNames` variant here —
    // matching against translations the step isn't written in caused unrelated
    // ingredients to leak into steps. Use the current-language name + English.
    const candidates = [
      ing.name,
      ing.product,
      ing.localizedName,
    ].filter(Boolean);
    let isMatch = false;

    for (const candidate of candidates) {
      const lower = String(candidate).toLowerCase().trim();
      if (!lower) continue;

      // Whole-phrase exact match first (cheap, catches multi-word names verbatim).
      if (wordBoundaryRe(lower).test(text)) {
        isMatch = true;
        break;
      }

      // Otherwise match significant words by stem, so inflected forms count
      // ("масло" ↔ "масла", "соль" ↔ "солью", "перец" ↔ "перцем").
      const ingStems = significantWords(candidate).map(stemWord).filter(Boolean);
      if (ingStems.some((is) => textWordStems.some((ts) => stemsMatch(is, ts)))) {
        isMatch = true;
        break;
      }
    }

    if (isMatch) {
      matched.push(ing);
      seen.add(key);
    }
  }

  return matched;
};

export default getStepIngredients;
